import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Lock, ShieldCheck, Activity, Brain, MapPin, CheckCircle2 } from 'lucide-react';

// Phase 34 — premium split-screen sign-in. Left: dark branded panel with
// animated backdrop + feature list + social proof. Right: the form. All auth
// logic (eager import, <Navigate> redirect, signIn, toast) is unchanged from
// Phase 16.1 / 20.1 — only the presentation is upgraded.

const Logo = ({ light = false }: { light?: boolean }) => (
  <div className="flex items-center gap-2.5">
    <svg width="34" height="34" viewBox="0 0 64 64" fill="none"><rect width="64" height="64" rx="15" fill="#2563EB" /><rect x="12" y="42" width="8" height="16" rx="2" fill="white" opacity=".5" /><rect x="24" y="32" width="8" height="26" rx="2" fill="white" opacity=".7" /><rect x="36" y="22" width="8" height="36" rx="2" fill="white" /><rect x="48" y="12" width="8" height="46" rx="2" fill="#22C55E" /></svg>
    <span className={'text-xl font-extrabold ' + (light ? 'text-white' : 'text-foreground')}>
      <span style={{ color: '#3b82f6' }}>Prodi</span>fy<span style={{ color: '#22c55e', fontSize: 13, verticalAlign: 'super' }}>360</span>
    </span>
  </div>
);

const FEATURES = [
  { icon: Activity, title: 'Real-time monitoring', desc: 'Live activity, screenshots & app usage' },
  { icon: Brain, title: 'AI productivity scoring', desc: 'Automatic insight, not guesswork' },
  { icon: MapPin, title: 'GPS & geofencing', desc: 'Know where field teams are working' },
  { icon: ShieldCheck, title: 'Enterprise-grade security', desc: 'Encrypted, compliant, audited' },
];

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const { signIn, user } = useAuth();
  const navigate = useSafeNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const fromPath = (location.state as { from?: string } | null)?.from || '/';
  if (user) return <Navigate to={fromPath} replace />;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast({ title: 'Missing details', description: 'Please enter your email and password', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await signIn(loginData.email, loginData.password);
      if (error) {
        toast({ title: 'Login failed', description: error.message, variant: 'destructive' });
      } else {
        navigate(fromPath, { replace: true });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Login failed', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* ── Left: branded panel (hidden on mobile) ── */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#0b0f1a] p-12 text-white">
        {/* animated backdrop */}
        <div className="absolute inset-0 auth-grid opacity-70" />
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-600/25 blur-3xl auth-blob" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-emerald-500/20 blur-3xl auth-blob" style={{ animationDelay: '5s' }} />

        <div className="relative z-10"><Logo light /></div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-[34px] leading-tight font-extrabold tracking-tight">
            The workforce productivity platform built for <span className="text-blue-400">modern teams</span>.
          </h1>
          <p className="mt-4 text-slate-400 text-[15px] leading-relaxed">
            Stop guessing. Measure what actually moves the needle — with real-time visibility your whole team can trust.
          </p>
          <ul className="mt-8 space-y-4">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex items-start gap-3">
                <span className="mt-0.5 h-9 w-9 shrink-0 rounded-xl bg-white/5 ring-1 ring-white/10 flex items-center justify-center text-blue-400">
                  <f.icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold">{f.title}</p>
                  <p className="text-xs text-slate-400">{f.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Trusted by 500+ companies</p>
          <div className="mt-3 flex items-center gap-8">
            <div><div className="text-2xl font-extrabold">500+</div><div className="text-[11px] text-slate-500">Companies</div></div>
            <div><div className="text-2xl font-extrabold">50,000+</div><div className="text-[11px] text-slate-500">Devices monitored</div></div>
            <div><div className="text-2xl font-extrabold text-emerald-400">32%</div><div className="text-[11px] text-slate-500">Avg. productivity gain</div></div>
          </div>
        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="flex items-center justify-center bg-background p-6 sm:p-10">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="lg:hidden mb-8 flex justify-center"><Logo /></div>

          <div className="mb-7">
            <h2 className="text-2xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your Prodify360 dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className="text-xs font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="login-email" type="email" autoComplete="email" placeholder="you@company.com" className="pl-10 h-11"
                  value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} disabled={isLoading} />
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="login-password" className="text-xs font-medium">Password</Label>
                <a href="/forgot-password" className="text-xs font-medium text-primary hover:underline">Forgot password?</a>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="login-password" type="password" autoComplete="current-password" placeholder="Enter your password" className="pl-10 h-11"
                  value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} disabled={isLoading} />
              </div>
            </div>
            <Button type="submit" className="w-full h-11 text-[15px] font-semibold shadow-lg shadow-primary/20 transition-transform hover:-translate-y-0.5" disabled={isLoading}>
              {isLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in…</>) : 'Sign in'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don&apos;t have an account? <a href="/register" className="text-primary font-semibold hover:underline">Start free trial</a>
          </p>
          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground/70 mt-6">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> 256-bit encryption · SOC2-aligned · GDPR ready
          </p>
        </div>
      </div>
    </div>
  );
}
